import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReactGA from "react-ga4";
import Drawer from 'react-modern-drawer';
import { BASE_PATH } from '../..';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/arrow-left.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import { ReactComponent as LoadingIcon } from '../../assets/icons/loading.svg';
import { ConfigContext } from '../../contexts/ConfigContext';
import { DialogContext } from '../../contexts/DialogContext';
import { Asset, Point, SceneDesignerContext } from '../../contexts/SceneDesignerContext';
import { GenerateODataFilter } from '../../helpers/odataFunctions';
import { BomModelService, MaterialService, ModelPackService, ModelService, ModelViewDTO, ModifierService } from '../../openapi/requests';
import AssetList from '../layout/AssetList';
import './ProductSelector.scss';
import { AuthContext } from '../../contexts/AuthContext';

interface Props {
    open: boolean;
    working?: boolean;
    point: Point | undefined;
    onCancel: () => void;
    onProductSelected: (products: Asset[]) => void;
    selectedProducts?: Asset[];
    disableMultiSelect?: boolean;
}

const addWarnings = (asset: Asset, point: Point, selectedBrand: string | undefined, selectedStyleDirection: string | undefined) => {
    if (asset.validFor.length !== point.configuration.placements.length) {
        asset.warning = 'This product is not supported in all configurations';
    }
    if (selectedBrand) {
        if (asset.metadata.some(m => m.name === 'brand') && !asset.metadata.some(m => m.name === 'brand' && m.value.toLowerCase() === selectedBrand.toLowerCase())) {
            asset.warning = `Not ${selectedBrand}`;
        }
    }
    if (selectedStyleDirection) {
        if (asset.metadata.some(m => m.name === 'style_direction') && !asset.metadata.some(m => m.name === 'style_direction' && m.value.toLowerCase() === selectedStyleDirection.toLowerCase())) {
            asset.warning = `Not ${selectedStyleDirection}`;
        }
    }
}

const sortByTitleAndWarning = (a: Asset, b: Asset) => {

    if (a.warning && !b.warning) {
        return 1;
    }
    if (b.warning && !a.warning) {
        return -1;
    }

    const nameA = a.title.toUpperCase();
    const nameB = b.title.toUpperCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }

    return 0;
}

const ProductSelector: React.FC<Props> = ({ open, onCancel, onProductSelected, point, working, disableMultiSelect, selectedProducts }) => {
    const { odataAssetFilters, odataFilters } = useContext(ConfigContext);
    const { selectedBrand, selectedStyleDirection, products } = useContext(SceneDesignerContext);
    const { hasPermission } = useContext(AuthContext);
    const { error } = useContext(DialogContext);
    const [items, setItems] = useState<Asset[]>([]);
    const [filteredItems, setFilteredItems] = useState<Asset[]>([]);
    const [query, setQuery] = useState("");
    const [selectedItems, setSelectedItems] = useState<Asset[]>([]);
    const [useMultiSelect, setUseMultiSelect] = useState(false);
    const [loading, setLoading] = useState(false);
    const [supportedParents, setSupportedParents] = useState<string[] | undefined>(undefined);

    const multiselectSupported = !selectedItems.some(e => e.type === "bommodel");

    useEffect(() => {
        if (point && !selectedProducts) {
            setSelectedItems(items.filter(e => point.configuration.uniqueValues.some(v => e.id === v.id)));
            setUseMultiSelect(point.configuration.uniqueValues.length > 1 && !disableMultiSelect);
        }
        if(selectedProducts){
            setSelectedItems(items.filter(e => selectedProducts.some(v => e.id === v.id)));
            setUseMultiSelect(selectedProducts.length > 1 && !disableMultiSelect);
        }
    }, [disableMultiSelect, items, point, selectedProducts]);

    useEffect(() => {
        if (!useMultiSelect) {
            if (selectedItems.length > 1) {
                setSelectedItems([selectedItems[0]]);
            }
        }
    }, [selectedItems, useMultiSelect]);

    useEffect(() => {
        setItems([]);
        setLoading(true);

        if (!open) {
            setLoading(false);
        }

        if (open && point && !loading) {
            if (point.type === 'model') {
                if (point.configuration.placement.useModelpack) {
                    let assets: Asset[] = [];
                    let promises = point.configuration.placements.map(p => ModelPackService.getModelPack(p.modelSelectionFilter || undefined, undefined, undefined, GenerateODataFilter(odataAssetFilters), undefined, 'title')
                        .then(result => {
                            result.value.forEach(a => {
                                var asset = assets.find(e => e.id === a.id);
                                if (asset && asset.type === 'modelpack') {
                                    asset.validFor.push(p);
                                } else {
                                    assets.push({ ...a, type: 'modelpack', validFor: [p] })
                                }
                            });
                        }));

                    Promise.all(promises).then(e => {
                        assets.forEach(e => addWarnings(e, point, selectedBrand, selectedStyleDirection));
                        assets.sort(sortByTitleAndWarning);
                        setLoading(false);
                        setItems(assets);
                    }).catch(reason => {
                        error(undefined, reason);
                    });
                } else {
                    let assets: Asset[] = [];
                    let filters = odataAssetFilters;
                    if(!hasPermission("EditBoms")){
                        filters = [...filters, {name: 'isWhitemodel', property: 'isWhitemodel', type: "select", values: [{value: false}]}]
                    }
                    let promises = [
                        ...point.configuration.placements.map(p => ModelService.getModel(p.modelSelectionFilter || undefined, undefined, undefined, GenerateODataFilter(filters), undefined, 'title')
                            .then(result => {
                                result.value.forEach(a => {
                                    var asset = assets.find(e => e.id === a.id);
                                    if (asset && asset.type === 'model') {
                                        asset.validFor.push(p);
                                    } else {
                                        assets.push({ ...a, type: 'model', validFor: [p] })
                                    }
                                });
                            })),
                        ...point.configuration.placements.map(p => BomModelService.getBomModel(p.modelSelectionFilter || undefined, undefined, undefined, GenerateODataFilter(odataAssetFilters), undefined, 'title')
                            .then(result => {
                                result.value.forEach(a => {
                                    var asset = assets.find(e => e.id === a.id);
                                    if (asset && asset.type === 'bommodel') {
                                        asset.validFor.push(p);
                                    } else {
                                        assets.push({ ...a, type: 'bommodel', validFor: [p] })
                                    }
                                });
                            }))];

                    Promise.all(promises).then(e => {
                        assets.forEach(e => addWarnings(e, point, selectedBrand, selectedStyleDirection));
                        assets.sort(sortByTitleAndWarning);
                        setLoading(false);
                        setItems(assets);
                    }).catch(reason => {
                        error(undefined, reason);
                    });
                }
            } else if (point.type === 'material') {
                let assets: Asset[] = [];
                let promises = point.configuration.placements.map(p => MaterialService.getMaterial(p.materialSelectionFilter || undefined, undefined, undefined, GenerateODataFilter(odataAssetFilters), undefined, 'title')
                    .then(result => {
                        result.value.forEach(a => {
                            var asset = assets.find(e => e.id === a.id);
                            if (asset && asset.type === 'material') {
                                asset.validFor.push(p);
                            } else {
                                assets.push({ ...a, type: 'material', validFor: [p] })
                            }
                        });
                    }));

                Promise.all(promises).then(e => {
                    assets.forEach(e => addWarnings(e, point, selectedBrand, selectedStyleDirection));
                    assets.sort(sortByTitleAndWarning);
                    setLoading(false);
                    setItems(assets);
                }).catch(reason => {
                    error(undefined, reason);
                });
            } else if (point.type === 'modifier') {
                let assets: Asset[] = [];
                let promises = point.configuration.placements.map(p => ModifierService.getModifier(p.modifierSelectionFilter || undefined, undefined, undefined, GenerateODataFilter(odataAssetFilters), undefined, 'title')
                    .then(result => {
                        result.value.forEach(a => {
                            var asset = assets.find(e => e.id === a.id);
                            if (asset && asset.type === 'modifier') {
                                asset.validFor.push(p);
                            } else {
                                assets.push({ ...a, type: 'modifier', validFor: [p] })
                            }
                        });
                    }));

                Promise.all(promises).then(e => {
                    assets.forEach(e => addWarnings(e, point, selectedBrand, selectedStyleDirection));
                    assets.sort(sortByTitleAndWarning);
                    setLoading(false);
                    setItems(assets);
                }).catch(reason => {
                    error(undefined, reason);
                });
            } else if (point.type === 'propset') {
                setItems(point.configuration.placement.options.map(e => ({ ...e, type: 'propset', validFor: [point.configuration.placement], metadata: [], tags: [] })));
                setLoading(false);
            }
        }
    }, [point, open, odataAssetFilters, odataFilters, selectedBrand, selectedStyleDirection, error]);

    useEffect(() => {

        let filtered = items;

        if (query) {
            var terms = query.toLowerCase().split(/[\s,;]+/);

            filtered = filtered.filter((e) => {
                if(e.title){
                    if(terms.some(t => e.title.toLowerCase().includes(t))){
                        return true;
                    }
                }
                if(e.metadata){
                    if(terms.some(t => e.metadata.some(m => m.value.toLowerCase() === t))){
                        return true;
                    }
                }

                return false;
            });
        }

        setFilteredItems(filtered);
    }, [items, query]);

    const parents = useMemo(() => {
        let parents: ModelViewDTO[] = [];

        if (point) {
            for (let i = 0; i < products.length; i++) {
                const element = products[i];
                const values = element.configuration.uniqueValues;

                for (let j = 0; j < values.length; j++) {
                    const model = values[j] as ModelViewDTO;

                    if (model.placementpoints) {
                        if (model.placementpoints.some(e => point.configuration.placements.some(p => p.name === e.name))) {
                            parents.push(model);
                        }
                    }
                    if (model.surfaces) {
                        if (model.surfaces.some(e => point.configuration.placements.some(p => p.name === e.name))) {
                            parents.push(model);
                        }
                    }
                }
            }
        }

        return parents;

    }, [point, products]);

    const handleDoubleClick = useCallback((product: Asset) => {
        ReactGA.event('click', { item: 'asset', location: 'productselector' });
        if (!useMultiSelect) {
            onProductSelected([product]);
        }
    }, [onProductSelected, useMultiSelect]);

    const handleClick = useCallback((product: Asset) => {
        ReactGA.event('click', { item: 'asset', location: 'productselector' });
    }, []);

    const handleDeleteClick = useCallback(() => {
        onProductSelected([]);
    }, [onProductSelected]);

    const handleSelectionChange = useCallback((products: Asset[]) => {
        setSelectedItems(products);
    }, []);

    const handleSubmit = useCallback(() => {
        onProductSelected(selectedItems);
    }, [onProductSelected, selectedItems]);

    const handleMouseEnter = useCallback((product: Asset) => {
        let supported = parents.filter(p =>
            p.placementpoints.some(e => product.validFor.some(v => v.name === e.name)) ||
            p.surfaces.some(e => product.validFor.some(v => v.name === e.name)));

        setSupportedParents(supported.map(e => e.name));
    }, [parents]);

    const handleMouseExit = useCallback(() => {
        setSupportedParents(undefined);
    }, []);

    return (
        <Drawer className='productselector' open={open} onClose={onCancel} direction='right' size={530} overlayOpacity={0} enableOverlay={false}>
            <div className='header'>
                <button className='back' onClick={onCancel}>
                    <ArrowLeftIcon />
                </button>
                <h1>Select item</h1>
                <button className='close' onClick={onCancel}>
                    <CloseIcon />
                </button>
            </div>

            {(parents.length !== 0) &&
                <div className='parents'>
                    <h2>Subproduct for</h2>
                    <div className='parent-items'>
                        {parents.map(e => <div className={classNames({ 'parent': true, 'unsupported': supportedParents && !supportedParents.includes(e.name) })}><img src={BASE_PATH + e.thumbnail} alt={e.title} /><p>{e.title}</p></div>)}
                    </div>
                </div>
            }
            <input type='search' placeholder='Search' value={query} onChange={(e) => setQuery(e.target.value)} />



            <AssetList<Asset>
                assets={filteredItems}
                onItemDoubleClick={handleDoubleClick}
                onSelectionChanged={handleSelectionChange}
                onItemMouseEnter={handleMouseEnter}
                onItemMouseExit={handleMouseExit}
                onItemClick={handleClick}
                selection={selectedItems}
                allowMultiSelect={useMultiSelect}
                className='productselector-items'
                loading={loading}
                displayMode={point?.type === 'propset' ? 'list' : 'medium'} />

            {!disableMultiSelect &&
                <div className='multiselect'>
                    <label>
                        <input disabled={!multiselectSupported} type="checkbox" checked={useMultiSelect} onChange={e => setUseMultiSelect(e.target.checked)} />
                        <span className="check">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3.5 8.5L6 11L12.5 4.5" stroke="currentcolor" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <span>Select multiple products</span>
                    </label>
                </div>
            }

            <div className='footer'>
                <button disabled={working} className='submit' onClick={handleSubmit}>
                    {working ? <LoadingIcon className='working' /> : 'Confirm'}
                </button>
                {!point?.isRequired &&
                    <button disabled={working} className='delete' onClick={handleDeleteClick}>
                        <DeleteIcon />
                    </button>
                }
            </div>
        </Drawer>
    );
};

export default ProductSelector;